import { useState, useEffect, useCallback } from "react";

// ─────────────────────────────────────────────
// SHARK AI — GTM Integration Panel
// Remote schema injection via Google Tag Manager
// No client site access required
// ─────────────────────────────────────────────

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

const STATUS = {
  IDLE: "idle",
  LOADING: "loading",
  SUCCESS: "success",
  ERROR: "error",
};

const STEPS = [
  { id: 1, label: "Connect GTM", desc: "Link client's GTM container" },
  { id: 2, label: "Configure Schema", desc: "Set event & business data" },
  { id: 3, label: "Deploy Tag", desc: "Push to GTM & publish" },
  { id: 4, label: "Verify Injection", desc: "Confirm live schema on site" },
];

export default function GTMIntegration({ client, onClose }) {
  const [step, setStep] = useState(1);
  const [gtmCreds, setGtmCreds] = useState({
    accountId: "",
    containerId: "",
    workspaceId: "1",
  });
  const [schemaConfig, setSchemaConfig] = useState({
    includeEvents: true,
    includeLocalBusiness: true,
    includeFAQ: false,
    eventFrequency: "monthly",
  });
  const [status, setStatus] = useState(STATUS.IDLE);
  const [log, setLog] = useState([]);
  const [verifyResult, setVerifyResult] = useState(null);
  const [tagId, setTagId] = useState(null);
  const [publishedVersion, setPublishedVersion] = useState(null);

  const addLog = useCallback((msg, type = "info") => {
    setLog((prev) => [
      ...prev,
      { msg, type, time: new Date().toLocaleTimeString() },
    ]);
  }, []);

  // ── Step 1: Connect GTM ──────────────────────────────────────────
  const connectGTM = async () => {
    if (!gtmCreds.accountId || !gtmCreds.containerId) {
      addLog("Account ID and Container ID are required.", "error");
      return;
    }
    setStatus(STATUS.LOADING);
    addLog("Verifying GTM container access...");
    try {
      const res = await fetch(`${API_BASE}/gtm/verify-access`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clientId: client.id, ...gtmCreds }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Access denied");
      addLog(`✓ Connected: ${data.containerName}`, "success");
      addLog(`✓ Workspace: ${data.workspaceName}`, "success");
      setStatus(STATUS.SUCCESS);
      setTimeout(() => setStep(2), 600);
    } catch (err) {
      addLog(`✗ ${err.message}`, "error");
      setStatus(STATUS.ERROR);
    }
  };

  // ── Step 3: Deploy Tag ───────────────────────────────────────────
  const deployTag = async () => {
    setStatus(STATUS.LOADING);
    addLog("Generating schema payload from Shark AI...");
    try {
      // 1. Generate schema
      const schemaRes = await fetch(`${API_BASE}/schema/${client.id}/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(schemaConfig),
      });
      const schemaData = await schemaRes.json();
      if (!schemaRes.ok) throw new Error(schemaData.error);
      addLog(`✓ Schema generated (${schemaData.types.join(", ")})`, "success");

      // 2. Push tag to GTM
      addLog("Creating GTM Custom HTML tag...");
      const tagRes = await fetch(`${API_BASE}/gtm/deploy-tag`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clientId: client.id,
          gtmCreds,
          schemaConfig,
        }),
      });
      const tagData = await tagRes.json();
      if (!tagRes.ok) throw new Error(tagData.error);
      setTagId(tagData.tagId);
      addLog(`✓ Tag created (ID: ${tagData.tagId})`, "success");

      // 3. Publish GTM version
      addLog("Publishing GTM container version...");
      const pubRes = await fetch(`${API_BASE}/gtm/publish`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clientId: client.id, gtmCreds }),
      });
      const pubData = await pubRes.json();
      if (!pubRes.ok) throw new Error(pubData.error);
      setPublishedVersion(pubData.version);
      addLog(`✓ Published: Version ${pubData.version.containerVersionId}`, "success");
      addLog("✓ Schema is now live on client site", "success");

      setStatus(STATUS.SUCCESS);
      setTimeout(() => setStep(4), 800);
    } catch (err) {
      addLog(`✗ ${err.message}`, "error");
      setStatus(STATUS.ERROR);
    }
  };

  // ── Step 4: Verify ───────────────────────────────────────────────
  const verifyInjection = async () => {
    setStatus(STATUS.LOADING);
    addLog(`Crawling ${client.website} for injected schema...`);
    try {
      const res = await fetch(`${API_BASE}/gtm/verify-injection`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clientId: client.id, website: client.website }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setVerifyResult(data);
      if (data.found) {
        addLog(`✓ Schema detected on live site`, "success");
        addLog(`✓ Types found: ${data.types.join(", ")}`, "success");
        data.warnings?.forEach((w) => addLog(`⚠ ${w}`, "warn"));
      } else {
        addLog("⚠ Schema not yet detected — GTM may need propagation time (2-5 min)", "warn");
      }
      setStatus(data.found ? STATUS.SUCCESS : STATUS.ERROR);
    } catch (err) {
      addLog(`✗ ${err.message}`, "error");
      setStatus(STATUS.ERROR);
    }
  };

  const logColor = { info: "#8899bb", success: "#34d399", error: "#f87171", warn: "#fbbf24" };

  return (
    <div style={styles.overlay}>
      <div style={styles.panel}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <div style={styles.sharkBadge}>⬡ SHARK AI</div>
            <div>
              <div style={styles.headerTitle}>GTM Schema Injection</div>
              <div style={styles.headerSub}>{client?.businessName || "Client"} · Remote Deploy</div>
            </div>
          </div>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* Step Progress */}
        <div style={styles.stepBar}>
          {STEPS.map((s, i) => (
            <div key={s.id} style={styles.stepWrap}>
              <div style={{
                ...styles.stepCircle,
                background: step > s.id ? "#34d399" : step === s.id ? "#3b82f6" : "#1e2a3a",
                border: step === s.id ? "2px solid #60a5fa" : "2px solid transparent",
              }}>
                {step > s.id ? "✓" : s.id}
              </div>
              <div style={styles.stepLabel}>{s.label}</div>
              {i < STEPS.length - 1 && (
                <div style={{
                  ...styles.stepLine,
                  background: step > s.id ? "#34d399" : "#1e2a3a",
                }} />
              )}
            </div>
          ))}
        </div>

        <div style={styles.body}>
          {/* ── STEP 1 ── */}
          {step === 1 && (
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Connect GTM Container</div>
              <div style={styles.sectionDesc}>
                Client grants you <strong>Publisher</strong> access in GTM. Enter their container details below — you'll control everything from here permanently.
              </div>
              <div style={styles.infoBox}>
                <span style={{ color: "#60a5fa" }}>ℹ</span> Find these in GTM under Admin → Container Settings
              </div>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>GTM Account ID</label>
                <input
                  style={styles.input}
                  placeholder="e.g. 1234567890"
                  value={gtmCreds.accountId}
                  onChange={(e) => setGtmCreds({ ...gtmCreds, accountId: e.target.value })}
                />
              </div>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Container ID</label>
                <input
                  style={styles.input}
                  placeholder="e.g. 9876543"
                  value={gtmCreds.containerId}
                  onChange={(e) => setGtmCreds({ ...gtmCreds, containerId: e.target.value })}
                />
              </div>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Workspace ID <span style={{ color: "#5a6a7a" }}>(default: 1)</span></label>
                <input
                  style={styles.input}
                  placeholder="1"
                  value={gtmCreds.workspaceId}
                  onChange={(e) => setGtmCreds({ ...gtmCreds, workspaceId: e.target.value })}
                />
              </div>
              <button
                style={{ ...styles.btn, opacity: status === STATUS.LOADING ? 0.6 : 1 }}
                onClick={connectGTM}
                disabled={status === STATUS.LOADING}
              >
                {status === STATUS.LOADING ? "Verifying..." : "Verify & Connect →"}
              </button>
            </div>
          )}

          {/* ── STEP 2 ── */}
          {step === 2 && (
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Configure Schema Types</div>
              <div style={styles.sectionDesc}>
                Select which schema types Shark AI will inject. These are generated from the client's profile data and updated automatically each month.
              </div>
              <div style={styles.toggleGrid}>
                {[
                  { key: "includeLocalBusiness", label: "LocalBusiness", desc: "NAP, hours, geo coords" },
                  { key: "includeEvents", label: "Event (Monthly)", desc: "Auto-generated each month" },
                  { key: "includeFAQ", label: "FAQ Schema", desc: "From client Q&A data" },
                ].map((item) => (
                  <div
                    key={item.key}
                    style={{
                      ...styles.toggleCard,
                      border: schemaConfig[item.key] ? "1px solid #3b82f6" : "1px solid #1e2a3a",
                      background: schemaConfig[item.key] ? "rgba(59,130,246,0.08)" : "#0d1520",
                    }}
                    onClick={() => setSchemaConfig({ ...schemaConfig, [item.key]: !schemaConfig[item.key] })}
                  >
                    <div style={styles.toggleDot(schemaConfig[item.key])} />
                    <div>
                      <div style={styles.toggleLabel}>{item.label}</div>
                      <div style={styles.toggleDesc}>{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Event Refresh Frequency</label>
                <select
                  style={styles.input}
                  value={schemaConfig.eventFrequency}
                  onChange={(e) => setSchemaConfig({ ...schemaConfig, eventFrequency: e.target.value })}
                >
                  <option value="monthly">Monthly (auto-publish 1st of month)</option>
                  <option value="weekly">Weekly</option>
                  <option value="manual">Manual only</option>
                </select>
              </div>
              <div style={styles.btnRow}>
                <button style={styles.btnGhost} onClick={() => setStep(1)}>← Back</button>
                <button style={styles.btn} onClick={() => setStep(3)}>Configure Tag →</button>
              </div>
            </div>
          )}

          {/* ── STEP 3 ── */}
          {step === 3 && (
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Deploy to GTM</div>
              <div style={styles.sectionDesc}>
                Shark AI will create a Custom HTML tag in GTM, set it to fire on All Pages, and publish a new container version — all remotely.
              </div>

              <div style={styles.previewBox}>
                <div style={styles.previewLabel}>Tag Preview</div>
                <pre style={styles.codeBlock}>{`<script>
(function() {
  fetch('${API_BASE}/schema/${client?.id || "CLIENT_ID"}')
    .then(r => r.json())
    .then(function(schema) {
      var s = document.createElement('script');
      s.type = 'application/ld+json';
      s.textContent = JSON.stringify(schema);
      document.head.appendChild(s);
    });
})();
</script>`}</pre>
              </div>

              <div style={styles.deployMeta}>
                <div style={styles.metaRow}>
                  <span style={styles.metaKey}>Tag name</span>
                  <span style={styles.metaVal}>SharkAI — GEO Schema Injection</span>
                </div>
                <div style={styles.metaRow}>
                  <span style={styles.metaKey}>Trigger</span>
                  <span style={styles.metaVal}>All Pages · DOM Ready</span>
                </div>
                <div style={styles.metaRow}>
                  <span style={styles.metaKey}>Schema types</span>
                  <span style={styles.metaVal}>
                    {[
                      schemaConfig.includeLocalBusiness && "LocalBusiness",
                      schemaConfig.includeEvents && "Event",
                      schemaConfig.includeFAQ && "FAQ",
                    ].filter(Boolean).join(", ")}
                  </span>
                </div>
                <div style={styles.metaRow}>
                  <span style={styles.metaKey}>Auto-refresh</span>
                  <span style={styles.metaVal}>{schemaConfig.eventFrequency}</span>
                </div>
              </div>

              {/* Activity Log */}
              {log.length > 0 && (
                <div style={styles.logBox}>
                  {log.map((entry, i) => (
                    <div key={i} style={{ color: logColor[entry.type], fontSize: 12, marginBottom: 3 }}>
                      <span style={{ color: "#3a4a5a", marginRight: 8 }}>{entry.time}</span>
                      {entry.msg}
                    </div>
                  ))}
                </div>
              )}

              <div style={styles.btnRow}>
                <button style={styles.btnGhost} onClick={() => setStep(2)}>← Back</button>
                <button
                  style={{ ...styles.btn, opacity: status === STATUS.LOADING ? 0.6 : 1 }}
                  onClick={deployTag}
                  disabled={status === STATUS.LOADING}
                >
                  {status === STATUS.LOADING ? "Deploying..." : "🚀 Deploy & Publish"}
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 4 ── */}
          {step === 4 && (
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Verify Live Injection</div>
              <div style={styles.sectionDesc}>
                Shark AI crawls the client's live site to confirm the schema tag is rendering correctly and is visible to AI crawlers.
              </div>

              {publishedVersion && (
                <div style={styles.successBanner}>
                  <div style={styles.successIcon}>✓</div>
                  <div>
                    <div style={{ fontWeight: 600, color: "#34d399" }}>GTM Version {publishedVersion.containerVersionId} Published</div>
                    <div style={{ fontSize: 12, color: "#5a7a6a", marginTop: 2 }}>{client?.website}</div>
                  </div>
                </div>
              )}

              {verifyResult && (
                <div style={styles.verifyGrid}>
                  <div style={styles.verifyCard(verifyResult.found)}>
                    <div style={styles.verifyIcon}>{verifyResult.found ? "✓" : "✗"}</div>
                    <div style={styles.verifyLabel}>Schema Detected</div>
                  </div>
                  <div style={styles.verifyCard(verifyResult.valid)}>
                    <div style={styles.verifyIcon}>{verifyResult.valid ? "✓" : "✗"}</div>
                    <div style={styles.verifyLabel}>Valid JSON-LD</div>
                  </div>
                  <div style={styles.verifyCard(verifyResult.crawlable)}>
                    <div style={styles.verifyIcon}>{verifyResult.crawlable ? "✓" : "⚠"}</div>
                    <div style={styles.verifyLabel}>AI Crawlable</div>
                  </div>
                  <div style={styles.verifyCard(verifyResult.geoScore > 70)}>
                    <div style={styles.verifyIcon}>{verifyResult.geoScore || "—"}</div>
                    <div style={styles.verifyLabel}>GEO Score</div>
                  </div>
                </div>
              )}

              {log.length > 0 && (
                <div style={styles.logBox}>
                  {log.map((entry, i) => (
                    <div key={i} style={{ color: logColor[entry.type], fontSize: 12, marginBottom: 3 }}>
                      <span style={{ color: "#3a4a5a", marginRight: 8 }}>{entry.time}</span>
                      {entry.msg}
                    </div>
                  ))}
                </div>
              )}

              <div style={styles.btnRow}>
                <button
                  style={{ ...styles.btnGhost, opacity: status === STATUS.LOADING ? 0.6 : 1 }}
                  onClick={verifyInjection}
                  disabled={status === STATUS.LOADING}
                >
                  {status === STATUS.LOADING ? "Crawling..." : "🔍 Run Verification"}
                </button>
                <button style={styles.btn} onClick={onClose}>
                  ✓ Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────
const styles = {
  overlay: {
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.75)",
    backdropFilter: "blur(4px)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1000,
  },
  panel: {
    width: 640, maxHeight: "90vh",
    background: "#080f18",
    border: "1px solid #1a2535",
    borderRadius: 16,
    overflow: "hidden",
    display: "flex", flexDirection: "column",
    boxShadow: "0 24px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(59,130,246,0.1)",
  },
  header: {
    padding: "20px 24px",
    borderBottom: "1px solid #1a2535",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    background: "#0a1220",
  },
  headerLeft: { display: "flex", alignItems: "center", gap: 14 },
  sharkBadge: {
    background: "linear-gradient(135deg, #1e3a5f, #0f2340)",
    border: "1px solid #2a4a7f",
    color: "#60a5fa",
    fontSize: 11, fontWeight: 700, letterSpacing: 2,
    padding: "6px 10px", borderRadius: 8,
  },
  headerTitle: { color: "#e2e8f0", fontWeight: 700, fontSize: 16 },
  headerSub: { color: "#4a6a8a", fontSize: 12, marginTop: 2 },
  closeBtn: {
    background: "none", border: "1px solid #1e2a3a",
    color: "#4a6a8a", width: 32, height: 32, borderRadius: 8,
    cursor: "pointer", fontSize: 14, display: "flex",
    alignItems: "center", justifyContent: "center",
  },
  stepBar: {
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: "20px 24px", gap: 0, background: "#0a1220",
    borderBottom: "1px solid #1a2535",
  },
  stepWrap: { display: "flex", alignItems: "center", gap: 0 },
  stepCircle: {
    width: 28, height: 28, borderRadius: "50%",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 12, fontWeight: 700, color: "#e2e8f0",
    flexShrink: 0, transition: "all 0.3s",
  },
  stepLabel: { color: "#4a6a8a", fontSize: 11, marginLeft: 8, marginRight: 8, whiteSpace: "nowrap" },
  stepLine: { width: 40, height: 2, transition: "background 0.3s" },
  body: { flex: 1, overflowY: "auto", padding: 24 },
  section: { display: "flex", flexDirection: "column", gap: 16 },
  sectionTitle: { color: "#e2e8f0", fontWeight: 700, fontSize: 18 },
  sectionDesc: { color: "#5a7a9a", fontSize: 13, lineHeight: 1.6 },
  infoBox: {
    background: "rgba(59,130,246,0.06)",
    border: "1px solid rgba(59,130,246,0.2)",
    borderRadius: 8, padding: "10px 14px",
    color: "#5a8aaa", fontSize: 12, display: "flex", gap: 8, alignItems: "center",
  },
  fieldGroup: { display: "flex", flexDirection: "column", gap: 6 },
  label: { color: "#7a9ab8", fontSize: 12, fontWeight: 600, letterSpacing: 0.5 },
  input: {
    background: "#0d1520", border: "1px solid #1e2a3a",
    borderRadius: 8, padding: "10px 14px",
    color: "#e2e8f0", fontSize: 14, outline: "none",
    fontFamily: "inherit",
    transition: "border-color 0.2s",
  },
  btn: {
    background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
    border: "none", borderRadius: 8,
    color: "#fff", padding: "12px 24px",
    fontSize: 14, fontWeight: 600, cursor: "pointer",
    transition: "opacity 0.2s",
    alignSelf: "flex-end",
  },
  btnGhost: {
    background: "none", border: "1px solid #1e2a3a",
    borderRadius: 8, color: "#5a7a9a",
    padding: "12px 24px", fontSize: 14, cursor: "pointer",
  },
  btnRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 },
  toggleGrid: { display: "flex", flexDirection: "column", gap: 10 },
  toggleCard: {
    display: "flex", alignItems: "center", gap: 14,
    padding: "14px 16px", borderRadius: 10, cursor: "pointer",
    transition: "all 0.2s",
  },
  toggleDot: (active) => ({
    width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
    background: active ? "#3b82f6" : "#1e2a3a",
    border: active ? "none" : "2px solid #2a3a4a",
    transition: "all 0.2s",
  }),
  toggleLabel: { color: "#c8d8e8", fontSize: 14, fontWeight: 600 },
  toggleDesc: { color: "#4a6a8a", fontSize: 12, marginTop: 2 },
  previewBox: {
    background: "#060d16", border: "1px solid #1a2535",
    borderRadius: 10, overflow: "hidden",
  },
  previewLabel: {
    padding: "8px 14px", background: "#0d1520",
    borderBottom: "1px solid #1a2535",
    color: "#3a5a7a", fontSize: 11, fontWeight: 700, letterSpacing: 1,
  },
  codeBlock: {
    margin: 0, padding: "14px 16px",
    color: "#5a9a7a", fontSize: 12, lineHeight: 1.7,
    overflowX: "auto", fontFamily: "monospace",
  },
  deployMeta: {
    background: "#0d1520", border: "1px solid #1a2535",
    borderRadius: 10, padding: "14px 16px",
    display: "flex", flexDirection: "column", gap: 10,
  },
  metaRow: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  metaKey: { color: "#3a5a7a", fontSize: 12 },
  metaVal: { color: "#8ab8d8", fontSize: 12, fontWeight: 600 },
  logBox: {
    background: "#060d16", border: "1px solid #1a2535",
    borderRadius: 8, padding: "12px 14px",
    maxHeight: 160, overflowY: "auto",
    fontFamily: "monospace",
  },
  successBanner: {
    background: "rgba(52,211,153,0.06)",
    border: "1px solid rgba(52,211,153,0.2)",
    borderRadius: 10, padding: "14px 16px",
    display: "flex", alignItems: "center", gap: 14,
  },
  successIcon: {
    width: 36, height: 36, borderRadius: "50%",
    background: "rgba(52,211,153,0.15)",
    color: "#34d399", display: "flex",
    alignItems: "center", justifyContent: "center",
    fontSize: 18, fontWeight: 700, flexShrink: 0,
  },
  verifyGrid: {
    display: "grid", gridTemplateColumns: "1fr 1fr",
    gap: 10,
  },
  verifyCard: (ok) => ({
    background: ok ? "rgba(52,211,153,0.06)" : "rgba(248,113,113,0.06)",
    border: `1px solid ${ok ? "rgba(52,211,153,0.2)" : "rgba(248,113,113,0.15)"}`,
    borderRadius: 10, padding: "16px",
    display: "flex", flexDirection: "column",
    alignItems: "center", gap: 8,
  }),
  verifyIcon: { fontSize: 22, fontWeight: 700, color: "#e2e8f0" },
  verifyLabel: { fontSize: 12, color: "#5a7a9a", textAlign: "center" },
};
